import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { ApolloClient } from "@apollo/client";
import {
  GET_NEGOTIATION_STRATEGY,
  GET_NEGOTIATION_TARGETS,
  GET_NEGOTIATION_TACTICS,
  GET_NEGOTIATION_ALTERNATIVES,
  GET_POWER_DYNAMICS_SCORE,
  NEGOTIATION_STRATEGY_SUBSCRIPTION,
} from "../graphql/targetAndTactics";
import { useToastStore } from "./toastStore";
import { fetchWithHeaders } from "../api/fetchWithHeaders";
import useLoaderStore from "./useLoaderStore";

// Types matching the new backend models
type TargetReasoning = {
  targetPriceReasoning?: string;
  llmReasoning?: string;
};

type StrategyTargetPrice = {
  mdo?: number;
  laa?: number;
  supplierTarget?: number;
  openingTarget?: number;
  internalTarget?: number;
  currentBidPrice?: number;
  currency?: string;
  openingAllocation?: number;
  targetAllocation?: number;
  batna?: string;
  reasoning?: TargetReasoning;
};

type BidInfo = {
  bidId: number;
  tenderId: number;
  supplierId?: number;
  supplierCode?: string;
  supplierName?: string;
  price?: number;
  quantity?: number;
  unitOfMeasure?: string;
  currency?: string;
  packageCost?: number;
  logisticCost?: number;
  exwPrice?: number;
  totalDdpPrice?: number;
  deliveryLocation?: string;
  paymentTerm?: string;
  rebateOffer?: string;
  volumeDiscount?: string;
  offeredIncoterm?: string;
  productDescription?: string;
  countryOfOrigin?: string;
  materialNumber?: string;
  country?: string;
  bidRound?: number;
  isCurrent?: boolean;
  submittedAt?: string;
};

type SourcingProduct = {
  tenderSourcingProductId: number;
  tenderId: number;
  variantId: string;
  productDescription: string;
  display: string;
  displayId: string;
  displayDescription: string;
  caseQuantity: number;
  displayQuantity?: string;
  sourcingQuantity?: string;
  sourcingCountry: string;
  bids: BidInfo[];
  target?: StrategyTargetPrice;
};

type DisplayVariant = {
  tenderSourcingProductId: number;
  variantId: string;
  variant?: string;
  productDescription: string;
  caseQuantity: number;
  displayQuantity?: string;
  sourcingCountry: string;
  sourcingQuantity?: string;
  bidPrice?: number;
  bidCurrency?: string;
  target?: StrategyTargetPrice;
};

type DisplayGroup = {
  display: string;
  displayId: string;
  displayDescription: string;
  totalCaseQuantity: number;
  mixPrice?: number;
  mixPriceCurrency?: string;
  variants: DisplayVariant[];
};


// New type for power dynamics assessment Q&A
type PowerDynamicsAssessmentItem = {
  id: number;
  party: string;  // 'buyer' or 'supplier'
  question: string;
  answer?: string;
  reasoning?: string;
  score?: number;
};

type NegotiationStrategy = {
  id: number;
  negotiationId: number;
  supplierId: number;
  targetPriceReasoning?: string;
  batna?: string;
  confidenceLevel?: string;
  ourLeverage?: string;
  supplierLeverage?: string;
  powerBalance?: string;
  powerBalanceReasoning?: string;
  // New power dynamics fields from SupplierAssessmentAgent
  buyerPowerScore?: number;
  supplierPowerScore?: number;
  powerDifferential?: number;
  ourPowerSummary?: string;
  supplierPowerSummary?: string;
  powerDynamicsAssessment?: PowerDynamicsAssessmentItem[];
  // End new power dynamics fields
  negotiationApproach?: string;
  approachSummary?: string;
  currentSegment?: string;
  targetSegment?: string;
  segmentRationale?: string;
  valueClaim?: boolean;
  valueCreation?: boolean;
  relationshipDepth?: string;
  sourcingProducts?: SourcingProduct[];
  displayGroups?: DisplayGroup[];
};

type NegotiationTarget = {
  id: number;
  negotiationId: number;
  supplierId: number;
  parameterName: string;
  objective: string;
  leverageLevel: string;
  rationale: string;
};

type NegotiationTactic = {
  id: number;
  negotiationId: number;
  supplierId: number;
  parameterName: string;
  maximumConcession: string;
  requiredTradeOff: string;
  walkAwayPoint: string;
  concessionRationale: string;
};

type ScriptStatus = {
  exists: boolean
}

type NegotiationAlternative = {
  id: number;
  negotiationId: number;
  supplierId: number;
  parameterName: string;
  maximumConcession: string;
  requiredTradeOff: string;
  walkAwayPoint: string;
  concessionRationale: string;
};

interface TargetStore {
  // Data
  strategy: NegotiationStrategy | null;
  targets: NegotiationTarget[];
  tactics: NegotiationTactic[];
  alternatives: NegotiationAlternative[];
  powerDynamicsScore: number;

  // Loading states
  strategyLoading: boolean;
  targetsLoading: boolean;
  createScriptLoading: boolean;
  tacticsLoading: boolean;
  alternativesLoading: boolean;
  scriptStatus:ScriptStatus,
  regeneratedTableData: {}
  groupByDisplay: boolean;


  // Errors
  error: string | null;
  _strategySub?: { unsubscribe: () => void } | null;

  // Actions
  fetchStrategy: (apolloClient: ApolloClient<any>, negotiationId: number, supplierId: number, tenderId?: number | null) => Promise<void>;
  fetchTargets: (apolloClient: ApolloClient<any>, strategyId: number) => Promise<void>;
  fetchTactics: (apolloClient: ApolloClient<any>, negotiationId: number, supplierId: number) => Promise<void>;
  fetchAlternatives: (apolloClient: ApolloClient<any>, strategyId: number) => Promise<void>;
  fetchPowerDynamicsScore: (apolloClient: ApolloClient<any>, negotiationId: number, supplierId: number, tenderId?: number | null) => Promise<void>;
  fetchAll: (apolloClient: ApolloClient<any>, negotiationId: number, supplierId: number, tenderId?: number | null) => Promise<void>;
  clearAll: () => void;
  setTarget: (data: any) => void;
  setLevers: (data: any) => void;
  setConcessions: (data: any) => void;
  setPowerDynamics: (data: { insights: any; score: number }) => void;
  // Subscription handlers
  updateTargetsFromSubscription: (targets: NegotiationTarget[]) => void;
  updateTacticsFromSubscription: (tactics: NegotiationTactic[]) => void;
  updatePowerDynamicsFromSubscription: (data: { score: number; insights: string; reasoning?: string }) => void;
  updateApproachFromSubscription: (items: string[]) => void;
  getScriptStatus: ( negotiationId: any, supplierId: any, tenderId?: any) => Promise<any>,
  regenerateScript: (data: any) => void;
  subscribeToStrategy: (
    apolloClient: ApolloClient<object>,
    negotiationId: string | number,
    supplierId: string | number,
    tenderId?: string | number | null
  ) => void;
  stopStrategySubscription: () => void;
  setGroupByDisplay: (value: boolean) => void;
}

const initialState = {
  strategy: null,
  targets: [],
  tactics: [],
  alternatives: [],
  powerDynamicsScore: 50,
  strategyLoading: false,
  targetsLoading: false,
  tacticsLoading: false,
  alternativesLoading: false,
  error: null,
  createScriptLoading: false,
  scriptStatus: {
    exists: false
  },
  regeneratedTableData: {},
  groupByDisplay: false

};

const targetANDtacticsStore = create<TargetStore>()(
  devtools((set, get) => ({
    ...initialState,
    
    fetchStrategy: async (apolloClient, negotiationId, supplierId, tenderId) => {
      set({ strategyLoading: true, error: null });
      const { showLoader } = useLoaderStore.getState();
      try {
        showLoader(true);
        const { data } = await apolloClient.query({
          query: GET_NEGOTIATION_STRATEGY,
          variables: { negotiationId, supplierId },
          fetchPolicy: 'network-only',
        });
        set({
          strategy: data.negotiationStrategy,
          strategyLoading: false,
        });

        // Also fetch power dynamics score when strategy is fetched
        await get().fetchPowerDynamicsScore(apolloClient, negotiationId, supplierId, tenderId);

        // (re)start live updates after initial fetch
        get().subscribeToStrategy(apolloClient, negotiationId, supplierId, tenderId);
      } catch (error) {
        console.error('Error fetching strategy:', error);
        set({
          strategyLoading: false,
          error: 'Failed to fetch strategy',
        });
      }
      finally {
        showLoader(false);
      }
    },

    subscribeToStrategy: (apolloClient, negotiationId, supplierId, _tenderId) => {
    // tear down existing sub if IDs changed or you're re-calling
    get().stopStrategySubscription();

    const observable = apolloClient.subscribe({
      query: NEGOTIATION_STRATEGY_SUBSCRIPTION,
      variables: { negotiationId, supplierId },
      fetchPolicy: 'network-only',
    });

    const sub = observable.subscribe({
      next: ({ data }) => {
        const update = data?.negotiationStrategyUpdated;
        if (!update) return;
        
        set({ strategy: update  }); 
      },
      error: (err) => {
        console.error('Strategy subscription error:', err);
        set({ error: 'Live updates disconnected' });
        // optional: retry logic or backoff is already handled by ws client above
      },
      complete: () => {
        // server closed the stream
      },
    });

    // hold the handle so we can unsubscribe later
    // @ts-ignore - store private field
    set({ _strategySub: sub });
  },

  stopStrategySubscription: () => {
    
    const sub = get()._strategySub;
    if (sub) {
      sub.unsubscribe();
      set({ _strategySub: null });
    }
  },
    
    fetchTargets: async (apolloClient, strategyId) => {
      const { showLoader } = useLoaderStore.getState();
      showLoader(true);
      set({ targetsLoading: true, error: null });
      try {
        const { data } = await apolloClient.query({
          query: GET_NEGOTIATION_TARGETS,
          variables: { strategyId },
          fetchPolicy: 'network-only',
        });
        set({ 
          targets: data.negotiationTargetsByStrategy,
          targetsLoading: false,
        });
      } catch (error) {
        console.error('Error fetching targets:', error);
        set({ 
          targetsLoading: false,
          error: 'Failed to fetch targets',
        });
      }
      finally {
        showLoader(false);
      }
    },
    
    fetchTactics: async (apolloClient, negotiationId, supplierId) => {
      const { showLoader } = useLoaderStore.getState();
      showLoader(true);
      set({ tacticsLoading: true, error: null });
      try {
        const { data } = await apolloClient.query({
          query: GET_NEGOTIATION_TACTICS,
          variables: { negotiationId, supplierId },
          fetchPolicy: 'network-only',
        });
        set({
          tactics: data.negotiationTactics,
          tacticsLoading: false,
        });
      } catch (error) {
        console.error('Error fetching tactics:', error);
        set({ 
          tacticsLoading: false,
          error: 'Failed to fetch tactics',
        });
      }
      finally {
        showLoader(false);
      }
    },
    
    fetchAlternatives: async (apolloClient, strategyId) => {
      const { showLoader } = useLoaderStore.getState();
      showLoader(true);
      set({ alternativesLoading: true, error: null });
      try {
        const { data } = await apolloClient.query({
          query: GET_NEGOTIATION_ALTERNATIVES,
          variables: { strategyId },
          fetchPolicy: 'network-only',
        });
        set({ 
          alternatives: data.negotiationAlternativesByStrategy,
          alternativesLoading: false,
        });
      } catch (error) {
        console.error('Error fetching alternatives:', error);
        set({ 
          alternativesLoading: false,
          error: 'Failed to fetch alternatives',
        });
      }
      finally {
        showLoader(false);
      }
    },
    
    fetchPowerDynamicsScore: async (apolloClient, negotiationId, supplierId, tenderId) => {
      const { showLoader } = useLoaderStore.getState();

      try {
        const { data } = await apolloClient.query({
          query: GET_POWER_DYNAMICS_SCORE,
          variables: { negotiationId, supplierId, tenderId },
          fetchPolicy: 'network-only',
        });
        set({ 
          powerDynamicsScore: data.powerDynamicsScore || 50,
        });
      } catch (error) {
        console.error('Error fetching power dynamics score:', error);
      }
      finally {
        showLoader(false);
      }
    },
    
    fetchAll: async (apolloClient, negotiationId, supplierId, tenderId) => {
      // First fetch strategy to get the strategy ID
      await get().fetchStrategy(apolloClient, negotiationId, supplierId, tenderId);
      const strategy = get().strategy;
      
      if (strategy) {
        // Fetch remaining data in parallel using strategy ID where applicable
        await Promise.all([
          get().fetchTargets(apolloClient, strategy.id),
          get().fetchTactics(apolloClient, negotiationId, supplierId),
          get().fetchAlternatives(apolloClient, strategy.id),
          get().fetchPowerDynamicsScore(apolloClient, negotiationId, supplierId, tenderId),
        ]);
      }
    },
    
    clearAll: () => {
      set(initialState);
    },
    
    setTarget: (data: any) => {
      set({ targets: data });
    },
    
    setLevers: (data: any) => {
      // Store levers data - you may need to adjust based on your actual data structure
      // For now, storing in alternatives as they seem related
      set({ alternatives: data });
    },
    
    setConcessions: (data: any) => {
      // Store concessions data - you may need to adjust based on your actual data structure
      // For now, storing in tactics as they seem related
      set({ tactics: data });
    },
    
    setPowerDynamics: (data: { insights: any; score: number }) => {
      set({ powerDynamicsScore: data.score });
    },
    
    // Subscription handlers
    updateTargetsFromSubscription: (targets: NegotiationTarget[]) => {
      set({ targets });
    },
    
    updateTacticsFromSubscription: (tactics: NegotiationTactic[]) => {
      set({ tactics });
    },
    
    updatePowerDynamicsFromSubscription: (data: { score: number; insights: string; reasoning?: string }) => {
      set({ powerDynamicsScore: data.score });
      
      // Update strategy with power balance reasoning if we have one
      const currentStrategy = get().strategy;
      if (currentStrategy) {
        set({ 
          strategy: { 
            ...currentStrategy, 
            powerBalance: data.insights || currentStrategy.powerBalance,
            powerBalanceReasoning: data.reasoning || currentStrategy.powerBalanceReasoning
          } 
        });
      }
    },
    
    updateApproachFromSubscription: (items: string[]) => {
      // Update strategy with new approach if we have one
      const currentStrategy = get().strategy;
      if (currentStrategy && items.length > 0) {
        set({ 
          strategy: { 
            ...currentStrategy, 
            negotiationApproach: items[0] 
          } 
        });
      }
    },

    getScriptStatus: async ( negotiationId: any, supplierId: any, tenderId?: any) => {
			const { error } = useToastStore.getState();

			const url = import.meta.env.VITE_API_BASE_URL;
			const params = new URLSearchParams({
				negotiation_id: String(negotiationId),
        supplier_id: String(supplierId),
			});
			// Only add tender_id if it's provided
			if (tenderId != null) {
				params.append('tender_id', String(tenderId));
			}
			try {
				const resp = await fetchWithHeaders(
					url + `api/v1/negotiation-packages/script_status?${params.toString()}`,
					{
						method: "GET",
					}
				);
				const data = await resp.json();
        set({scriptStatus: data})

				return data;

			} catch (err) {
				error("Error", `Failed to fetch reports: ${err}`);
			} finally {
			}
		},


    regenerateScript: async (payload:any) => {
        const url = import.meta.env.VITE_API_BASE_URL;
        const { error } = useToastStore.getState();
        set({createScriptLoading: true});
        try {
          const response = await fetchWithHeaders(`${url}api/v1/negotiation-packages/create_script`, {
            method: 'POST',
            body: JSON.stringify(payload)
          });
          const data = await response.json();

          if (!data?.success) {
            error("Error", `Failed to generate script: ${data?.message}`);
            return null;
          };
          set({regeneratedTableData: data.payload});
          return data;
        } catch (error) {
          return null;
        } finally {
          set({createScriptLoading: false});
        }
      },

    setGroupByDisplay: (value: boolean) => {
      set({ groupByDisplay: value });
    }
  }))
);

export default targetANDtacticsStore;

// Export types for use in components
export type {
  SourcingProduct,
  BidInfo,
  StrategyTargetPrice,
  TargetReasoning,
  DisplayGroup,
  DisplayVariant
};